import { computed } from "vue";
import { useCurrentTranslationDiffSnapshotsStore } from "@/features/experience-shared/stores/useCurrentTranslationDiffSnapshotsStore";
import { mapDataToHighlights } from "@/features/experience-highlights/lib/mapDataToHighlights";
import type { HighlightsKey, MixedHighlightValue } from "@/types/Highlights";
import type { Snapshot } from "@/types/Snapshots";
import type { CustomHighlights } from "@/types/generated/ExperienceRawServiceApi";

/** `HighlightsKey` -> functional-information key holding its premade codes. */
const functionalKeyByHighlight: Record<
  HighlightsKey,
  "highlights" | "important_information" | "included" | "excluded"
> = {
  highlights: "highlights",
  important_information: "important_information",
  included: "included",
  non_included: "excluded",
};

/** `HighlightsKey` -> commercial `customs` key holding its custom entries. */
const customKeyByHighlight: Record<
  HighlightsKey,
  "custom_highlights" | "custom_important_information" | "custom_included" | "custom_non_included"
> = {
  highlights: "custom_highlights",
  important_information: "custom_important_information",
  included: "custom_included",
  non_included: "custom_non_included",
};

function getFunctional(snapshot: Snapshot | undefined, highlightKey: HighlightsKey): MixedHighlightValue {
  const customs = snapshot?.experience_commercial_information?.translations?.find(
    (t) => t.language_code === "en"
  )?.customs;

  return mapDataToHighlights(
    highlightKey,
    snapshot?.experience_functional_information?.[functionalKeyByHighlight[highlightKey]]?.codes ?? [],
    (customs?.[customKeyByHighlight[highlightKey]] ?? []) as CustomHighlights[]
  );
}

/** English/source and pre-publication (diff) highlight content for one field, from the diff snapshots. */
export function useTranslationSourceHighlights(highlightKey: HighlightsKey) {
  const diffSnapshotsStore = useCurrentTranslationDiffSnapshotsStore();

  const source = computed(() => getFunctional(diffSnapshotsStore.latestEnglishSnapshot, highlightKey));
  const diffOld = computed(() => getFunctional(diffSnapshotsStore.lastEnglishSnapshotBeforePublication, highlightKey));

  const hasDiff = computed(() => diffSnapshotsStore.hasDiff);

  return { source, diffOld, hasDiff };
}
